import type React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import { Col, Container, Row, SpacerRow } from '@zg-rentals/multibrand-ui';
import Section from '@rental-manager/components/Section';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import HandShake from 'assets/Handshake.svg';
import ProblemTriangle from '@zg-rentals/multibrand-ui/lib/icons/ProblemTriangle';
import ContentBox3 from '@zg-rentals/multibrand-ui/lib/ContentBox3';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import StringConstants from '@common/constants/StringConstants';
import { ExpandableDisclaimer } from '@zillow/zrs-comet';
import type { IStyledTextModel } from '@rental-manager/models/Types';
import DebouncedButton from '@common/components/DebouncedButton';

type RentalProtectionDisclaimers = Array<IStyledTextModel>;

type Props = {
  onClose: () => void;
  onBack: () => void;
  addPaymentManually: ({ featureName, on }) => void;
  propertyId: string;
  fromLeaseSelectorPage: boolean;
  listingAlias: string;
  rentalProtectionDisclaimers: RentalProtectionDisclaimers;
};

const AreYouSurePage: React.FC<React.PropsWithChildren<Props>> = (props: Props): React.ReactElement => {
  const { listingAlias, propertyId } = props;
  const managePolicyUrl = StringConstants.LINKS.RENTAL_PROTECTION.MANAGE_POLICY_PAGE(listingAlias);

  const addLeaseNow = () => {
    window.location.href = StringConstants.LINKS.GO_TO_LEASES(propertyId);
  };

  const addPaymentManually = () => {
    props.addPaymentManually({
      featureName: FeaturePermissionTypes.RENT_GUARANTEE,
      on: false,
    });
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="Payment setup"
      onClickBack={props.fromLeaseSelectorPage ? props.onBack : null}
      onRequestClose={props.onClose}
      dataAutoTestId="landlord-rental-protection-are-you-sure-modal"
    >
      {
        <Container>
          <Section>
            <ProblemTriangle className="centeredImg" colorType="action" size="lg" />
          </Section>
          <Section>
            <Text type="h4" className="txtC zillowInsuranceSubHeading" emphasize>
              Zillow Insurance Services
            </Text>
            <Text type="h3" className="txtC">
              Continue without an eligible lease?
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Text>
              If you set up payments without a lease signed by an applicant you marked eligible, you won&apos;t be able
              to request&nbsp;
              <Link escape={false} target="_blank" to={managePolicyUrl} type="secondary">
                Rental Protection
              </Link>
              .
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <ContentBox3>
              <Row>
                <Col md={2}>
                  <img src={HandShake as unknown as string} alt="" />
                </Col>
                <Col md={10}>
                  <Text inline type="body-small" className="boldText">
                    Create or upload a lease
                  </Text>
                  <Text type="body-small">
                    We guide you through each step, so you can quickly customize your lease with your property in mind.
                  </Text>
                  <SpacerRow size="sm" />
                  <Text>
                    <Link onClick={addLeaseNow} target="_blank">
                      Add a lease
                    </Link>
                  </Text>
                </Col>
              </Row>
            </ContentBox3>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <ExpandableDisclaimer disclaimerContent={props.rentalProtectionDisclaimers} title="Legal Disclaimer" />
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <DebouncedButton onClick={addPaymentManually} type="primary" full>
              Yes, continue
            </DebouncedButton>
          </Section>
        </Container>
      }
    </Modal>
  );
};

export default AreYouSurePage;
